import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import PrecisionManufacturingOutlinedIcon from '@mui/icons-material/PrecisionManufacturingOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import MapView from '../map/MapView';
import Sidebar, { DEFAULT_SIDEBAR_PROPS } from '../common/Sidebar';
import Header from '../common/Header';
import BlockingLoadingOverlay from '../common/BlockingLoadingOverlay';
import DenseEntityCardBody from '../cards/DenseEntityCardBody';
import { VerifyPayload, OrderBundle, DriverInfo } from '../../api/wmService';
import { fetchReworldVerifyPayload } from '../../modules/reworld/viewAdapters';
import { routingJobsApi } from '../../api/routingJobsApi';
import { useNavigate } from 'react-router-dom';
import { TenantModelDefinition } from '../../modules/tenantRegistry';
import { TouchPoint, TaskInfo } from '../../models/types';
import {
  buildTouchPointCardModel,
  buildTripCardModel,
} from '../../presentation/cards/models';
import type { DenseHeaderMetricModel } from '../../presentation/cards/types';
import CompactSearchSortControl, {
  CompactSortDirection,
  CompactSortOption,
} from '../common/CompactSearchSortControl';

interface VerifyTaskViewProps {
  hubId: string;
  taskId: string;
  initialPayload?: VerifyPayload | null;
  fetchVerifyPayload?: (depotId: string, jobId: string) => Promise<VerifyPayload>;
  tenantDefinition?: TenantModelDefinition | null;
}

type VerifyStepId = 'orders' | 'drivers' | 'equipment';
type OrderSortKey =
  | 'soId'
  | 'asset'
  | 'uom'
  | 'wasteType'
  | 'weight'
  | 'pincode'
  | 'touchPoints'
  | 'address';
type DriverSortKey = 'name' | 'class' | 'shiftStartTime' | 'capacity' | 'plate';
type EquipmentSortKey = 'type' | 'capacityWeight' | 'capacityVolume' | 'fuelType';

type VerifyStepDefinition = {
  id: VerifyStepId;
  label: string;
  description: string;
  confirmLabel: string;
  accent: string;
  icon: React.ReactNode;
};

const tonnesLabel = (kg: number): string => `${(kg / 1000).toFixed(2)} t`;
const capacityLabel = (kg?: number): string => `${((kg ?? 0) / 1000).toFixed(2)} t`;
const kgLabel = (kg: number): string => `${Math.round(kg)} kg`;

const normalizeWeightKg = (value: number, uom?: string): number => {
  const normalized = (uom || '').toLowerCase();
  if (normalized.includes('lb')) {
    return value * 0.453592;
  }
  if (normalized.includes('ton')) {
    return value * 1000;
  }
  return value;
};

const parseClockToMinutes = (value?: string): number | null => {
  if (!value || !value.includes(':')) return null;
  const [hoursText, minsText] = value.split(':');
  const hours = Number(hoursText);
  const minutes = Number(minsText);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
};

const formatClockLabel = (value?: string): string => {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  const match = raw.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return raw;
  return `${match[1].padStart(2, '0')}:${match[2]}`;
};

const buildWindowLabel = (from?: string, to?: string): string => {
  const formattedFrom = formatClockLabel(from);
  const formattedTo = formatClockLabel(to);
  if (formattedFrom && formattedTo) return `${formattedFrom} - ${formattedTo}`;
  return formattedFrom || formattedTo || '—';
};

const compareSortValues = (
  left: string | number,
  right: string | number,
  direction: CompactSortDirection
): number => {
  if (typeof left === 'number' && typeof right === 'number') {
    return direction === 'asc' ? left - right : right - left;
  }
  const normalizedLeft = String(left ?? '');
  const normalizedRight = String(right ?? '');
  const comparison = normalizedLeft.localeCompare(normalizedRight, undefined, {
    numeric: true,
    sensitivity: 'base',
  });
  return direction === 'asc' ? comparison : -comparison;
};

type VerifyPincodeGroup = {
  id: string;
  pinCode: string;
  label: string;
  orders: OrderBundle[];
  touchPoints: TouchPoint[];
  totalWeightKg: number;
  touchPointCount: number;
  centerLngLat: [number, number];
  boundaryLngLats: [number, number][];
};

const normalizePinCode = (value?: string): string => {
  const normalized = String(value ?? '').trim();
  return normalized || 'UNSPECIFIED';
};

const coordinateKey = (lngLat: [number, number]): string => `${lngLat[0].toFixed(6)}:${lngLat[1].toFixed(6)}`;

const averageLngLat = (points: [number, number][]): [number, number] => {
  if (!points.length) return [0, 0];
  const totals = points.reduce(
    (acc, point) => {
      acc[0] += point[0];
      acc[1] += point[1];
      return acc;
    },
    [0, 0]
  );
  return [totals[0] / points.length, totals[1] / points.length];
};

const offsetLngLatByMeters = (
  lngLat: [number, number],
  eastMeters: number,
  northMeters: number
): [number, number] => {
  const [lng, lat] = lngLat;
  const dLat = northMeters / 111320;
  const dLng = eastMeters / (111320 * Math.cos((lat * Math.PI) / 180));
  return [lng + dLng, lat + dLat];
};

const convexHull = (points: [number, number][]): [number, number][] => {
  const unique = Array.from(new Map(points.map((point) => [coordinateKey(point), point])).values());
  if (unique.length === 1) {
    const center = unique[0];
    const synthetic = [
      center,
      offsetLngLatByMeters(center, 650, 0),
      offsetLngLatByMeters(center, -320, 560),
      offsetLngLatByMeters(center, -320, -560),
    ];
    return [...synthetic, synthetic[0]];
  }
  if (unique.length === 2) {
    const [left, right] = unique;
    const mid: [number, number] = [(left[0] + right[0]) / 2, (left[1] + right[1]) / 2];
    const synthetic = offsetLngLatByMeters(mid, 0, 700);
    return [left, right, synthetic, left];
  }

  const sorted = [...unique].sort((left, right) => {
    if (left[0] !== right[0]) return left[0] - right[0];
    return left[1] - right[1];
  });

  const cross = (origin: [number, number], a: [number, number], b: [number, number]) =>
    (a[0] - origin[0]) * (b[1] - origin[1]) - (a[1] - origin[1]) * (b[0] - origin[0]);

  const lower: [number, number][] = [];
  sorted.forEach((point) => {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], point) <= 0) {
      lower.pop();
    }
    lower.push(point);
  });

  const upper: [number, number][] = [];
  [...sorted].reverse().forEach((point) => {
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], point) <= 0) {
      upper.pop();
    }
    upper.push(point);
  });

  const hull = lower.slice(0, -1).concat(upper.slice(0, -1));
  return hull.length >= 3 ? [...hull, hull[0]] : [];
};

const VerifyTaskView: React.FC<VerifyTaskViewProps> = ({
  hubId,
  taskId,
  initialPayload = null,
  fetchVerifyPayload = fetchReworldVerifyPayload,
  tenantDefinition,
}) => {
  const navigate = useNavigate();
  const isGemba = tenantDefinition?.key === 'gemba:gemba_slotted';
  const isJumboBuy = tenantDefinition?.key.startsWith('jumbobuy:') ?? false;

  const [loading, setLoading] = useState(initialPayload == null);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<VerifyPayload | null>(initialPayload);
  const [runAlgoLoading, setRunAlgoLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [panelStep, setPanelStep] = useState<VerifyStepId | null>(null);
  const [completedSteps, setCompletedSteps] = useState<VerifyStepId[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [orderSortKey, setOrderSortKey] = useState<OrderSortKey>(isGemba ? 'weight' : isJumboBuy ? 'weight' : 'asset');
  const [orderSortDir, setOrderSortDir] = useState<CompactSortDirection>(isGemba || isJumboBuy ? 'desc' : 'asc');
  const [driverAvailability, setDriverAvailability] = useState<Record<string, boolean>>({});
  const [equipmentAvailability, setEquipmentAvailability] = useState<Record<string, boolean>>({});
  const [driverSearch, setDriverSearch] = useState('');
  const [driverSortKey, setDriverSortKey] = useState<DriverSortKey>(isGemba ? 'capacity' : 'name');
  const [driverSortDir, setDriverSortDir] = useState<CompactSortDirection>('asc');
  const [equipmentSearch, setEquipmentSearch] = useState('');
  const [equipmentSortKey, setEquipmentSortKey] = useState<EquipmentSortKey>('type');
  const [equipmentSortDir, setEquipmentSortDir] = useState<CompactSortDirection>('asc');
  const [focusLngLat, setFocusLngLat] = useState<[number, number] | null>(null);
  const [externalTouchPoint, setExternalTouchPoint] = useState<TouchPoint | null>(null);
  const [activeTouchPointKey, setActiveTouchPointKey] = useState<string | null>(null);
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
  const [geoCorrectionTouchPoint, setGeoCorrectionTouchPoint] = useState<TouchPoint | null>(null);
  const [geoCorrectionDraftLngLat, setGeoCorrectionDraftLngLat] = useState<[number, number] | null>(null);
  const [geoCorrectionSubmitting, setGeoCorrectionSubmitting] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [expandedPincodeIds, setExpandedPincodeIds] = useState<Record<string, boolean>>({});

  const verifySteps = useMemo<VerifyStepDefinition[]>(() => {
    const shared = [
      {
        id: 'orders' as const,
        label: 'Verify order',
        description: 'Review order weight, address and delivery timing.',
        confirmLabel: 'Confirm',
        accent: '#0f766e',
        icon: <Inventory2OutlinedIcon fontSize="small" />,
      },
      {
        id: 'drivers' as const,
        label: 'Verify drivers',
        description: isGemba ? 'Review vehicle capacity and plate readiness.' : 'Review driver readiness before verification.',
        confirmLabel: 'Confirm',
        accent: '#1d4ed8',
        icon: <LocalShippingOutlinedIcon fontSize="small" />,
      },
    ];
    if (isGemba || isJumboBuy) {
      return shared;
    }
    return [
      ...shared,
      {
        id: 'equipment' as const,
        label: 'Verify equipment',
        description: 'Review supporting equipment and finalize verification.',
        confirmLabel: 'Confirm',
        accent: '#7c3aed',
        icon: <PrecisionManufacturingOutlinedIcon fontSize="small" />,
      },
    ];
  }, [isGemba, isJumboBuy]);

  useEffect(() => {
    setCompletedSteps([]);
    setPanelStep(null);
    setSelectedOrderId(null);
    setActiveGroupId(null);
    setExpandedPincodeIds({});
    setSearchTerm('');
    setDriverSearch('');
    setEquipmentSearch('');
    setOrderSortKey(isGemba ? 'weight' : isJumboBuy ? 'weight' : 'asset');
    setDriverSortKey(isGemba ? 'capacity' : 'name');
  }, [isGemba, isJumboBuy, taskId]);

  const hydratePayload = (payload: VerifyPayload) => {
    setData(payload);
    const nextDriverAvailability: Record<string, boolean> = {};
    (payload.drivers || []).forEach((driver) => {
      nextDriverAvailability[driver.driverId] = driver.available ?? true;
    });
    setDriverAvailability(nextDriverAvailability);

    const nextEquipmentAvailability: Record<string, boolean> = {};
    (payload.equipment || []).forEach((equipment: any) => {
      nextEquipmentAvailability[equipment.equipmentId] = equipment.available ?? true;
    });
    setEquipmentAvailability(nextEquipmentAvailability);
    setLoading(false);
  };

  useEffect(() => {
    setError(null);
    setExternalTouchPoint(null);
    setGeoCorrectionTouchPoint(null);
    setGeoCorrectionDraftLngLat(null);
    setActiveTouchPointKey(null);
    if (initialPayload) {
      hydratePayload(initialPayload);
      return;
    }
    setLoading(true);
    fetchVerifyPayload(hubId, taskId)
      .then((payload) => hydratePayload(payload))
      .catch((err: any) => {
        setError(err?.message || 'Failed to fetch verify payload');
        setLoading(false);
      });
  }, [fetchVerifyPayload, hubId, initialPayload, taskId]);

  const jumboBuyPincodeGroups = useMemo<VerifyPincodeGroup[]>(() => {
    if (!data || !isJumboBuy) return [];

    const groups = new Map<string, VerifyPincodeGroup>();

    data.orders.forEach((order) => {
      const pinCode = normalizePinCode(order.pinCode);
      const groupId = `PIN-${pinCode}`;
      const dropLngLat: [number, number] = [order.drop.lng, order.drop.lat];
      const existing = groups.get(groupId) ?? {
        id: groupId,
        pinCode,
        label: pinCode === 'UNSPECIFIED' ? 'No pincode' : pinCode,
        orders: [],
        touchPoints: [],
        totalWeightKg: 0,
        touchPointCount: 0,
        centerLngLat: dropLngLat,
        boundaryLngLats: [],
      };

      const dropTouchPoint: TouchPoint = {
        tpId: `D-${order.soId}-${order.drop.id}`,
        seqNo: existing.touchPoints.length + 1,
        name: order.buyerName || order.soId,
        address: data.lookups?.drops?.[order.drop.id]?.address || order.drop.address,
        zipCode: pinCode === 'UNSPECIFIED' ? '' : pinCode,
        lngLat: dropLngLat,
        attemptTime: { start: Number.NaN, end: Number.NaN },
        openHoursMinOfDay: [],
        slotTags: ['DROP'],
        timeBreachMin: 0,
        itemSummary: {
          dropWtKg: normalizeWeightKg(order.qty, order.uom),
          pickWtKg: 0,
          volCuFt: 0,
          gmv: 0,
          dropItemCount: 1,
          pickItemCount: 0,
          distinctItemTags: order.wasteType ? [order.wasteType] : [],
        },
        orderNumber: order.soId,
        customerName: order.buyerName,
        timeWindowRaw: buildWindowLabel(order.fromTime, order.toTime),
        clusterTag: 'VERIFY_PINCODE',
        geoCorrected: order.geoCorrected ?? false,
        geoStatus: order.geoStatus,
        geoCorrectionRef: order.dropGeoCorrection?.ref,
        geoCorrectionRole: order.dropGeoCorrection?.role,
        geoCorrectionSupported: order.dropGeoCorrection?.editable ?? false,
      };

      existing.orders.push(order);
      existing.touchPoints.push(dropTouchPoint);
      existing.totalWeightKg += normalizeWeightKg(order.qty, order.uom);
      existing.touchPointCount += 1;
      groups.set(groupId, existing);
    });

    return Array.from(groups.values()).map((group) => {
      const points = group.touchPoints.map((touchPoint) => touchPoint.lngLat);
      return {
        ...group,
        centerLngLat: averageLngLat(points),
        boundaryLngLats: convexHull(points),
      };
    });
  }, [data, isJumboBuy]);

  const minimalTaskInfo: TaskInfo | null = useMemo(() => {
    if (!data) return null;
    const origin = data.lookups?.origin;
    const originLngLat: [number, number] = [data.origin.lng, data.origin.lat];

    if (isJumboBuy) {
      return {
        taskId,
        userId: 'verify-user',
        tenant: 'jumbobuy',
        status: 'UNVERIFIED',
        config: {
          id: 'verify-config',
          algoType: 'VERIFY',
          sourceLngLat: originLngLat,
          geoBarriers: [],
        },
        editPerf: {
          editPercent: 0,
          tpPerTrip: 0,
          wtPerTripKg: 0,
          distPerTripKm: 0,
          tpsWithoutTrip: 0,
          noOfTrips: 0,
          cpk: 0,
          cpTp: 0,
          tripsWithTimeBreach: 0,
          noOfSlotSlippage: 0,
          tpPerTripDelta: 0,
          wtPerTripKgDelta: 0,
          distPerTripKmDelta: 0,
          tpsWithoutTripDelta: 0,
          noOfTripsDelta: 0,
          cpkDelta: 0,
          cpTpDelta: 0,
          tripsWithTimeBreachDelta: 0,
          noOfSlotSlippageDelta: 0,
          ctoGmv: 0,
          ctoGmvDelta: 0,
        },
        trips: jumboBuyPincodeGroups.map((group, index) => ({
          name: group.id,
          vehTripNo: index + 1,
          distKm: 0,
          totalWtKg: group.totalWeightKg,
          volCuFt: 0,
          slotTags: [group.label],
          geoMarkers: {
            tripBoundaryLngLats: group.boundaryLngLats,
            tripCenterLngLat: group.centerLngLat,
          },
          breachesAndRatios: {},
          tripCostPerf: {},
          vehDetails: {},
          tpDetails: group.touchPoints,
          tripMeta: {
            status: 'VERIFY_GROUP',
            extra: {
              pinCode: group.label,
              buyerCount: group.orders.length,
            },
          },
        })) as any,
      };
    }

    const trips = data.orders.map((order, index) => {
      const client = data.lookups?.clients[order.pickup.id];
      const drop = data.lookups?.drops[order.drop.id];
      const pickupWindowStart = parseClockToMinutes(order.fromTime);
      const pickupWindowEnd = parseClockToMinutes(order.toTime);
      const dropTouchPoint: TouchPoint = {
        tpId: `D-${order.soId}-${order.drop.id}`,
        seqNo: isGemba ? 1 : 3,
        name: `DROP ${order.soId}`,
        address: drop?.address || order.drop.address,
        zipCode: '',
        lngLat: [order.drop.lng, order.drop.lat],
        attemptTime: { start: Number.NaN, end: Number.NaN },
        openHoursMinOfDay:
          pickupWindowStart != null && pickupWindowEnd != null
            ? [{ start: pickupWindowStart, end: pickupWindowEnd }]
            : [],
        slotTags: ['DROP'],
        timeBreachMin: 0,
        itemSummary: {
          dropWtKg: normalizeWeightKg(order.qty, order.uom),
          pickWtKg: 0,
          volCuFt: 0,
          gmv: 0,
          dropItemCount: 1,
          pickItemCount: 0,
          distinctItemTags: order.wasteType ? [order.wasteType] : [],
        },
        orderNumber: order.soId,
        deliveryDurationMinutes: order.deliveryDurationMinutes,
        timeWindowRaw: buildWindowLabel(order.fromTime, order.toTime),
        geoCorrectionRef: order.dropGeoCorrection?.ref,
        geoCorrectionRole: order.dropGeoCorrection?.role,
        geoCorrectionSupported: order.dropGeoCorrection?.editable ?? false,
      };

      if (isGemba) {
        return {
          name: order.soId,
          vehTripNo: index + 1,
          distKm: 0,
          totalWtKg: normalizeWeightKg(order.qty, order.uom),
          volCuFt: 0,
          slotTags: [],
          geoMarkers: {
            tripBoundaryLngLats: [],
            tripCenterLngLat: [order.drop.lng, order.drop.lat] as [number, number],
          },
          breachesAndRatios: {},
          tripCostPerf: {},
          vehDetails: {},
          tpDetails: [dropTouchPoint],
        };
      }

      return {
        name: order.soId,
        vehTripNo: index + 1,
        distKm: 0,
        totalWtKg: normalizeWeightKg(order.qty, order.uom),
        volCuFt: 0,
        slotTags: [],
        geoMarkers: {
          tripBoundaryLngLats: [],
          tripCenterLngLat: originLngLat,
        },
        breachesAndRatios: {},
        tripCostPerf: {},
        vehDetails: {},
        tpDetails: [
          {
            tpId: `ORIGIN-${order.soId}`,
            seqNo: 1,
            name: `ORIGIN ${order.soId}`,
            address: origin?.address || 'Origin',
            zipCode: origin?.zip ? String(origin.zip) : '',
            lngLat: originLngLat,
            attemptTime: { start: Number.NaN, end: Number.NaN },
            openHoursMinOfDay: [],
            slotTags: ['ORIGIN'],
            timeBreachMin: 0,
            itemSummary: {
              dropWtKg: 0,
              pickWtKg: 0,
              volCuFt: 0,
              gmv: 0,
              dropItemCount: 0,
              pickItemCount: 0,
              distinctItemTags: [],
            },
            geoCorrectionRef: data.origin.originId,
            geoCorrectionRole: 'ORIGIN',
            geoCorrectionSupported: false,
          },
          {
            tpId: `P-${order.soId}-${order.pickup.id}`,
            seqNo: 2,
            name: `PICKUP ${order.soId}`,
            address: client?.address || order.pickup.address,
            zipCode: '',
            lngLat: [order.pickup.lng, order.pickup.lat],
            attemptTime: { start: Number.NaN, end: Number.NaN },
            openHoursMinOfDay:
              pickupWindowStart != null && pickupWindowEnd != null
                ? [{ start: pickupWindowStart, end: pickupWindowEnd }]
                : [],
            slotTags: ['PICKUP'],
            timeBreachMin: 0,
            itemSummary: {
              dropWtKg: 0,
              pickWtKg: normalizeWeightKg(order.qty, order.uom),
              volCuFt: 0,
              gmv: 0,
              dropItemCount: 0,
              pickItemCount: 1,
              distinctItemTags: order.wasteType ? [order.wasteType] : [],
            },
            geoCorrectionRef: order.pickupGeoCorrection?.ref,
            geoCorrectionRole: order.pickupGeoCorrection?.role,
            geoCorrectionSupported: order.pickupGeoCorrection?.editable ?? false,
          },
          dropTouchPoint,
        ],
      };
    });

    return {
      taskId,
      userId: 'verify-user',
      tenant: data.origin.name,
      status: 'UNVERIFIED',
      config: {
        id: 'verify-config',
        algoType: 'VERIFY',
        sourceLngLat: originLngLat,
        geoBarriers: [],
      },
      editPerf: {
        editPercent: 0,
        tpPerTrip: 0,
        wtPerTripKg: 0,
        distPerTripKm: 0,
        tpsWithoutTrip: 0,
        noOfTrips: 0,
        cpk: 0,
        cpTp: 0,
        tripsWithTimeBreach: 0,
        noOfSlotSlippage: 0,
        tpPerTripDelta: 0,
        wtPerTripKgDelta: 0,
        distPerTripKmDelta: 0,
        tpsWithoutTripDelta: 0,
        noOfTripsDelta: 0,
        cpkDelta: 0,
        cpTpDelta: 0,
        tripsWithTimeBreachDelta: 0,
        noOfSlotSlippageDelta: 0,
        ctoGmv: 0,
        ctoGmvDelta: 0,
      },
      trips: trips as any,
    };
  }, [data, isGemba, isJumboBuy, jumboBuyPincodeGroups, taskId]);

  const equipmentMarkers = useMemo(() => {
    return (data?.equipment || [])
      .filter((equipment: any) => equipmentAvailability[equipment.equipmentId] !== false)
      .map((equipment: any) => ({
        id: equipment.equipmentId,
        lngLat: [equipment.lng, equipment.lat] as [number, number],
        type: equipment.type,
      }));
  }, [data, equipmentAvailability]);

  const visibleTrips = useMemo(() => {
    if (isJumboBuy) {
      return jumboBuyPincodeGroups.map((group) => group.id);
    }
    return data ? data.orders.map((order) => order.soId) : [];
  }, [data, isJumboBuy, jumboBuyPincodeGroups]);

  const touchPointKey = (touchPoint: TouchPoint | null | undefined) =>
    touchPoint ? `${touchPoint.tpId}::${touchPoint.seqNo}` : null;

  const resolveVerifyTouchPoint = (order: OrderBundle, kind: 'origin' | 'pickup' | 'drop'): TouchPoint | null => {
    if (isJumboBuy) {
      const pinCode = normalizePinCode(order.pinCode);
      const group = jumboBuyPincodeGroups.find((candidate) => candidate.pinCode === pinCode);
      return group?.touchPoints.find((touchPoint) => touchPoint.orderNumber === order.soId) ?? null;
    }
    const trip = minimalTaskInfo?.trips?.find((candidate) => candidate.name === order.soId);
    if (!trip) return null;
    const slotTag = kind === 'origin' ? 'ORIGIN' : kind === 'pickup' ? 'PICKUP' : 'DROP';
    return trip.tpDetails.find((touchPoint) => touchPoint.slotTags?.includes(slotTag)) ?? null;
  };

  const orderMetaById = useMemo(() => {
    const map = new Map<string, { address: string; windowLabel: string }>();
    (data?.orders || []).forEach((order) => {
      map.set(order.soId, {
        address: data?.lookups?.drops?.[order.drop.id]?.address || order.drop.address,
        windowLabel: buildWindowLabel(order.fromTime, order.toTime),
      });
    });
    return map;
  }, [data]);

  const verifyStats = useMemo(() => {
    const orders = data?.orders || [];
    const fleet = data?.fleet || [];
    return {
      distinctOrders: new Set(orders.map((order) => order.soId)).size,
      totalOrderWeightKg: orders.reduce((sum, order) => sum + normalizeWeightKg(order.qty, order.uom), 0),
      driverCount: (data?.drivers || []).length,
      totalCapacityKg: fleet.reduce((sum, vehicle) => sum + normalizeWeightKg(vehicle.capacity, vehicle.uom), 0),
    };
  }, [data]);

  const maxBounds = useMemo(() => {
    if (!data) return null;
    const [lng, lat] = [data.origin.lng, data.origin.lat];
    return [
      [lng - 2.4, lat - 2.0],
      [lng + 2.4, lat + 2.0],
    ] as [[number, number], [number, number]];
  }, [data]);

  const orderSortOptions: CompactSortOption<OrderSortKey>[] = isGemba
    ? [
        { value: 'weight', label: 'Weight' },
        { value: 'soId', label: 'Order ID' },
        { value: 'address', label: 'Address' },
      ]
    : isJumboBuy
      ? [
          { value: 'weight', label: 'Weight' },
          { value: 'touchPoints', label: 'Touch points' },
          { value: 'pincode', label: 'Pincode' },
        ]
      : [
          { value: 'asset', label: 'Required asset' },
          { value: 'soId', label: 'SO ID' },
          { value: 'weight', label: 'Weight' },
          { value: 'uom', label: 'UoM' },
          { value: 'wasteType', label: 'Waste type' },
        ];

  const driverSortOptions: CompactSortOption<DriverSortKey>[] = isGemba
    ? [
        { value: 'capacity', label: 'Vehicle capacity' },
        { value: 'plate', label: 'Licence plate' },
      ]
    : [
        { value: 'name', label: 'Name' },
        { value: 'class', label: 'Class' },
        { value: 'shiftStartTime', label: 'Shift start' },
      ];

  const equipmentSortOptions: CompactSortOption<EquipmentSortKey>[] = [
    { value: 'type', label: 'Type' },
    { value: 'capacityWeight', label: 'Capacity weight' },
    { value: 'capacityVolume', label: 'Capacity volume' },
    { value: 'fuelType', label: 'Fuel type' },
  ];

  const filteredOrders = useMemo(() => {
    if (isJumboBuy) {
      return data?.orders || [];
    }
    const orders = data?.orders || [];
    const q = searchTerm.trim().toLowerCase();
    const filtered = orders.filter((order) => {
      if (!q) return true;
      if (isGemba) {
        const address = orderMetaById.get(order.soId)?.address || '';
        return order.soId.toLowerCase().includes(q) || address.toLowerCase().includes(q);
      }
      return (
        order.soId.toLowerCase().includes(q) ||
        (order.wasteType || '').toLowerCase().includes(q) ||
        (order.requiredAsset || '').toLowerCase().includes(q) ||
        (order.uom || '').toLowerCase().includes(q)
      );
    });

    return [...filtered].sort((left, right) => {
      const resolveOrderSortValue = (order: OrderBundle): string | number => {
        if (orderSortKey === 'weight') {
          return normalizeWeightKg(order.qty, order.uom);
        }
        if (orderSortKey === 'soId') {
          return order.soId;
        }
        if (orderSortKey === 'address') {
          return orderMetaById.get(order.soId)?.address || order.drop.address || '';
        }
        if (orderSortKey === 'asset') {
          return order.requiredAsset || '';
        }
        if (orderSortKey === 'uom') {
          return order.uom || '';
        }
        return order.wasteType || '';
      };

      return compareSortValues(resolveOrderSortValue(left), resolveOrderSortValue(right), orderSortDir);
    });
  }, [data, isGemba, isJumboBuy, orderMetaById, orderSortDir, orderSortKey, searchTerm]);

  const visibleJumboBuyPincodeGroups = useMemo(() => {
    if (!isJumboBuy) return [];
    const q = searchTerm.trim().toLowerCase();
    const filtered = jumboBuyPincodeGroups.filter((group) => {
      if (!q) return true;
      if (group.label.toLowerCase().includes(q)) return true;
      return group.orders.some((order) =>
        [order.soId, order.buyerName, order.drop.address].some((value) => String(value || '').toLowerCase().includes(q))
      );
    });

    return [...filtered].sort((left, right) => {
      if (orderSortKey === 'pincode') {
        return compareSortValues(left.label, right.label, orderSortDir);
      }
      if (orderSortKey === 'touchPoints') {
        return compareSortValues(left.touchPointCount, right.touchPointCount, orderSortDir);
      }
      return compareSortValues(left.totalWeightKg, right.totalWeightKg, orderSortDir);
    });
  }, [isJumboBuy, jumboBuyPincodeGroups, orderSortDir, orderSortKey, searchTerm]);

  const visibleDrivers = useMemo(() => {
    const drivers = data?.drivers || [];
    const q = driverSearch.trim().toLowerCase();
    const filtered = drivers.filter((driver) => {
      if (!q) return true;
      if (isGemba) {
        return (driver.vehicleNumber || driver.name || '').toLowerCase().includes(q);
      }
      const inName = (driver.name || '').toLowerCase().includes(q);
      const inClass = (driver.class || '').toLowerCase().includes(q);
      const inLicence = (driver.licenceClasses || []).some((item) => item.toLowerCase().includes(q));
      const inVehicleType = (driver.allowedVehicleTypes || []).some((item) => item.toLowerCase().includes(q));
      return inName || inClass || inLicence || inVehicleType;
    });

    return [...filtered].sort((left, right) => {
      let leftValue: string | number = '';
      let rightValue: string | number = '';
      if (isGemba) {
        if (driverSortKey === 'capacity') {
          leftValue = left.vehicleCapacityKg ?? 0;
          rightValue = right.vehicleCapacityKg ?? 0;
        } else {
          leftValue = left.vehicleNumber || left.name || '';
          rightValue = right.vehicleNumber || right.name || '';
        }
      } else {
        if (driverSortKey === 'name') {
          leftValue = left.name || '';
          rightValue = right.name || '';
        } else if (driverSortKey === 'class') {
          leftValue = left.class || '';
          rightValue = right.class || '';
        } else {
          leftValue = parseClockToMinutes(left.shiftStartTime) ?? (left.shiftStartTime || '');
          rightValue = parseClockToMinutes(right.shiftStartTime) ?? (right.shiftStartTime || '');
        }
      }

      return compareSortValues(leftValue, rightValue, driverSortDir);
    });
  }, [data, driverSearch, driverSortDir, driverSortKey, isGemba]);

  const visibleEquipment = useMemo(() => {
    const equipment = data?.equipment || [];
    const q = equipmentSearch.trim().toLowerCase();
    const filtered = equipment.filter((item: any) => {
      const inType = (item.type || '').toLowerCase().includes(q);
      const inId = (item.equipmentId || '').toLowerCase().includes(q);
      const inFleet = (item.fleetNumber || '').toLowerCase().includes(q);
      const inWaste = (item.wasteTypeAllowed || []).some((waste: string) => waste.toLowerCase().includes(q));
      return !q || inType || inId || inFleet || inWaste;
    });
    return [...filtered].sort((left: any, right: any) => {
      let leftValue: string | number = '';
      let rightValue: string | number = '';
      if (equipmentSortKey === 'type') {
        leftValue = left.type || '';
        rightValue = right.type || '';
      } else if (equipmentSortKey === 'capacityWeight') {
        leftValue = left.capacity_weight_kg || 0;
        rightValue = right.capacity_weight_kg || 0;
      } else if (equipmentSortKey === 'capacityVolume') {
        leftValue = left.capacity_volume_m3 || 0;
        rightValue = right.capacity_volume_m3 || 0;
      } else {
        leftValue = left.fuel_type || '';
        rightValue = right.fuel_type || '';
      }
      return compareSortValues(leftValue, rightValue, equipmentSortDir);
    });
  }, [data, equipmentSearch, equipmentSortDir, equipmentSortKey]);

  const hasWorkspaceData = Boolean(data && minimalTaskInfo);
  const workspaceError = !loading && (error || !data || !minimalTaskInfo) ? error || 'No verification data available' : null;

  const stepIndexById = useMemo(() => {
    return Object.fromEntries(verifySteps.map((step, index) => [step.id, index])) as Record<VerifyStepId, number>;
  }, [verifySteps]);

  const isStepCompleted = (stepId: VerifyStepId): boolean => completedSteps.includes(stepId);
  const isStepUnlocked = (stepId: VerifyStepId): boolean => {
    const index = stepIndexById[stepId];
    if (index === 0) return true;
    return isStepCompleted(verifySteps[index - 1].id);
  };

  const focusTouchPoint = (touchPoint: TouchPoint | null) => {
    if (!touchPoint) return;
    setExternalTouchPoint(touchPoint);
    setActiveTouchPointKey(touchPointKey(touchPoint));
    setFocusLngLat(touchPoint.lngLat);
  };

  const startGeoCorrection = (touchPoint: TouchPoint) => {
    if (touchPoint.geoCorrectionSupported === false) return;
    setExternalTouchPoint(touchPoint);
    setActiveTouchPointKey(touchPointKey(touchPoint));
    setGeoCorrectionTouchPoint(touchPoint);
    setGeoCorrectionDraftLngLat(null);
  };

  const cancelGeoCorrection = () => {
    setGeoCorrectionTouchPoint(null);
    setGeoCorrectionDraftLngLat(null);
  };

  const confirmGeoCorrection = async () => {
    if (!geoCorrectionTouchPoint || !geoCorrectionDraftLngLat || !tenantDefinition?.adaptVerifyPayloadFromViews) {
      return;
    }
    try {
      setGeoCorrectionSubmitting(true);
      const response = await routingJobsApi.correctGeoLocation(hubId, taskId, {
        screen: 'verify',
        touchPointRef: geoCorrectionTouchPoint.geoCorrectionRef ?? geoCorrectionTouchPoint.tpId,
        locationType: geoCorrectionTouchPoint.geoCorrectionRole,
        latitude: geoCorrectionDraftLngLat[1],
        longitude: geoCorrectionDraftLngLat[0],
      });
      const viewsById = Object.fromEntries((response.views ?? []).map((view) => [view.viewId, view]));
      const updatedPayload = tenantDefinition.adaptVerifyPayloadFromViews(response.job, viewsById);
      setData(updatedPayload);
      setExternalTouchPoint(null);
      setFocusLngLat(geoCorrectionDraftLngLat);
      setSnackbar({ open: true, message: response.message, severity: 'success' });
      cancelGeoCorrection();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || err?.message || 'Failed to update geo location',
        severity: 'error',
      });
    } finally {
      setGeoCorrectionSubmitting(false);
    }
  };

  const handleOpenStep = (stepId: VerifyStepId) => {
    if (!isStepUnlocked(stepId) || isStepCompleted(stepId)) return;
    setPanelStep(stepId);
  };

  const handleConfirmStep = async (stepId: VerifyStepId) => {
    const stepIndex = stepIndexById[stepId];
    const isFinalStep = stepIndex === verifySteps.length - 1;
    if (!isFinalStep) {
      setCompletedSteps((prev) => (prev.includes(stepId) ? prev : [...prev, stepId]));
      setPanelStep(null);
      setSnackbar({
        open: true,
        message: `${verifySteps[stepIndex].label} confirmed`,
        severity: 'success',
      });
      return;
    }

    setRunAlgoLoading(true);
    try {
      await routingJobsApi.verifyJob(hubId, taskId);
      setSnackbar({ open: true, message: 'Task verified successfully', severity: 'success' });
      setTimeout(() => navigate(`/task-summary/hub/${hubId}`), 700);
    } catch (err: any) {
      setSnackbar({ open: true, message: err?.message || 'Failed to verify inputs', severity: 'error' });
    } finally {
      setRunAlgoLoading(false);
    }
  };

  const activeStepDefinition = panelStep ? verifySteps.find((step) => step.id === panelStep) ?? null : null;
  const panelWidth = isGemba ? { xs: 'calc(100vw - 48px)', sm: 344 } : { xs: 'calc(100vw - 48px)', sm: 500 };

  const verifyMetricStripItems: DenseHeaderMetricModel[] = hasWorkspaceData
    ? [
        {
          key: 'distinctOrders',
          label: 'Touch points',
          value: String(verifyStats.distinctOrders),
          tooltipDescription: 'Distinct touch points loaded into the verification workspace.',
        },
        {
          key: 'totalWeight',
          label: 'Total weight',
          value: tonnesLabel(verifyStats.totalOrderWeightKg),
          tooltipDescription: 'Combined weight of all orders currently visible for verification.',
        },
        {
          key: 'drivers',
          label: 'Vehicles',
          value: String(verifyStats.driverCount),
          tooltipDescription: 'Available drivers or vehicle records in this verification workspace.',
        },
        {
          key: 'capacity',
          label: 'Capacity',
          value: tonnesLabel(verifyStats.totalCapacityKg),
          tooltipDescription: 'Combined capacity across the available fleet records.',
        },
      ]
    : [];

  const renderOrderCards = () => (
    <Stack spacing={1.2}>
      {filteredOrders.map((order) => {
        const orderMeta = orderMetaById.get(order.soId);
        const dropTouchPoint = resolveVerifyTouchPoint(order, 'drop');
        const isSelected = selectedOrderId === order.soId;
        const weightKg = normalizeWeightKg(order.qty, order.uom);

        if (isJumboBuy) {
          return null;
        }

        if (isGemba) {
          return (
            <Paper
              key={order.soId}
              variant="outlined"
              onClick={() => {
                setSelectedOrderId(order.soId);
                focusTouchPoint(dropTouchPoint);
              }}
              sx={{
                p: 1.4,
                cursor: 'pointer',
                borderRadius: 3,
                borderColor: isSelected ? '#0f766e' : 'divider',
                boxShadow: isSelected ? '0 16px 32px rgba(15,118,110,0.14)' : 'none',
                transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 14px 26px rgba(15,23,42,0.10)',
                },
              }}
            >
              <Stack spacing={1.2}>
                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: -0.2 }}>
                      {order.soId}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Order ID
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.8} flexWrap="wrap" justifyContent="flex-end">
                    <Chip label={kgLabel(weightKg)} size="small" sx={{ bgcolor: 'rgba(15,118,110,0.12)', color: '#0f766e', fontWeight: 700 }} />
                  </Stack>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <PlaceOutlinedIcon fontSize="small" sx={{ mt: 0.1, color: 'text.secondary' }} />
                  <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, lineHeight: 1.45 }}>
                    {orderMeta?.address || order.drop.address}
                  </Typography>
                </Stack>
                <Chip
                  label={`Window ${orderMeta?.windowLabel || buildWindowLabel(order.fromTime, order.toTime)}`}
                  size="small"
                  sx={{ alignSelf: 'flex-start', bgcolor: 'rgba(245,158,11,0.12)', color: '#b45309', fontWeight: 700 }}
                />
              </Stack>
            </Paper>
          );
        }

        const client = data?.lookups?.clients?.[order.pickup.id];
        const drop = data?.lookups?.drops?.[order.drop.id];
        return (
          <Paper
            key={order.soId}
            variant="outlined"
            onClick={() => {
              setSelectedOrderId(order.soId);
              focusTouchPoint(dropTouchPoint);
            }}
            sx={{
              p: 1.5,
              cursor: 'pointer',
              borderRadius: 3,
              borderColor: isSelected ? '#1d4ed8' : 'divider',
              transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 14px 26px rgba(15,23,42,0.10)',
              },
            }}
          >
            <Stack spacing={1.2}>
              <Stack direction="row" justifyContent="space-between" spacing={1}>
                <Stack direction="row" spacing={0.8} flexWrap="wrap" alignItems="center">
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {order.soId}
                  </Typography>
                  <Chip label={order.requiredAsset || 'No asset'} size="small" />
                  <Chip label={order.wasteType || 'No waste type'} size="small" />
                  <Chip label={`${order.qty} ${order.uom}`} size="small" sx={{ fontWeight: 700 }} />
                </Stack>
                <Stack direction="row" spacing={0.8} flexWrap="wrap" justifyContent="flex-end">
                  <Chip label={buildWindowLabel(order.fromTime, order.toTime)} size="small" sx={{ bgcolor: 'rgba(29,78,216,0.08)', color: '#1d4ed8', fontWeight: 700 }} />
                </Stack>
              </Stack>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Pickup: {client?.address || order.pickup.address}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Drop: {drop?.address || order.drop.address}
              </Typography>
            </Stack>
          </Paper>
        );
      })}
      {isJumboBuy &&
        visibleJumboBuyPincodeGroups.map((group) => {
          const isExpanded = expandedPincodeIds[group.id] ?? false;
          const isActive = activeGroupId === group.id;
          const groupTrip = minimalTaskInfo?.trips.find((candidate) => candidate.name === group.id);
          const groupModel = groupTrip
            ? buildTripCardModel(groupTrip, {
                tenant: minimalTaskInfo?.tenant,
                isVerifyGroup: true,
              })
            : null;
          return (
            <Paper
              key={group.id}
              variant="outlined"
              sx={{
                borderRadius: 3,
                borderColor: isActive ? '#0f766e' : 'divider',
                boxShadow: isActive ? '0 16px 32px rgba(15,118,110,0.14)' : 'none',
                transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 14px 26px rgba(15,23,42,0.10)',
                },
              }}
            >
              <Stack spacing={1.2}>
                <Box sx={{ p: 1.35 }}>
                  {groupModel ? (
                    <Box
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        setActiveGroupId(group.id);
                        setSelectedOrderId(null);
                        setFocusLngLat(group.centerLngLat);
                        setExternalTouchPoint(null);
                        setActiveTouchPointKey(null);
                      }}
                    >
                      <DenseEntityCardBody
                        model={groupModel}
                        density="compact"
                        metricLayout="row"
                        singleLineHighlightTags
                        singleLinePrimaryMetrics
                        singleLineCapacityTags
                        hideMetricDetails
                        actions={
                          <Button
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              setExpandedPincodeIds((prev) => ({
                                ...prev,
                                [group.id]: !isExpanded,
                              }));
                            }}
                            sx={{ minWidth: 0, px: 0.8 }}
                          >
                            {isExpanded ? 'Hide' : 'Buyers'}
                          </Button>
                        }
                      />
                    </Box>
                  ) : null}
                </Box>

                <Collapse in={isExpanded}>
                  <Stack spacing={1} sx={{ px: 1.1, pb: 1.15 }}>
                    {group.orders.map((order) => {
                      const buyerTouchPoint = group.touchPoints.find((touchPoint) => touchPoint.orderNumber === order.soId) ?? null;
                      const isBuyerSelected = selectedOrderId === order.soId;
                      const buyerModel = buyerTouchPoint
                        ? buildTouchPointCardModel(buyerTouchPoint, {
                            tenant: minimalTaskInfo?.tenant,
                            addressOverride: order.drop.address,
                            typeLabelOverride: 'Buyer',
                          })
                        : null;
                      return (
                        <Paper
                          key={order.soId}
                          variant="outlined"
                          onClick={() => {
                            setActiveGroupId(group.id);
                            setSelectedOrderId(order.soId);
                            focusTouchPoint(buyerTouchPoint);
                          }}
                          sx={{
                            cursor: 'pointer',
                            borderRadius: 2.5,
                            borderColor: isBuyerSelected ? '#1d4ed8' : 'divider',
                            backgroundColor: isBuyerSelected ? 'rgba(29,78,216,0.05)' : 'transparent',
                          }}
                        >
                          <Box sx={{ p: 1.05 }}>
                            {buyerModel ? (
                              <DenseEntityCardBody
                                model={buyerModel}
                                density="compact"
                                compactTitle
                                metricLayout="row"
                                singleLinePrimaryMetrics
                                hideMetricDetails
                                contentOrder={['primaryMetrics', 'highlightTags', 'capacityTags', 'breachTags', 'footer']}
                              />
                            ) : (
                              <Stack spacing={0.65}>
                                <Stack direction="row" justifyContent="space-between" spacing={1}>
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                      {order.buyerName || order.soId}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                      {order.soId}
                                    </Typography>
                                  </Box>
                                  <Chip label={kgLabel(normalizeWeightKg(order.qty, order.uom))} size="small" />
                                </Stack>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                  {order.drop.address}
                                </Typography>
                              </Stack>
                            )}
                          </Box>
                        </Paper>
                      );
                    })}
                  </Stack>
                </Collapse>
              </Stack>
            </Paper>
          );
        })}
      {((isJumboBuy && visibleJumboBuyPincodeGroups.length === 0) || (!isJumboBuy && filteredOrders.length === 0)) && (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            No orders match the current filters.
          </Typography>
        </Box>
      )}
    </Stack>
  );

  const renderDriverCards = () => {
    if (isGemba) {
      return (
        <Stack spacing={1.2}>
          {visibleDrivers.map((driver: DriverInfo) => (
            <Paper key={driver.driverId} variant="outlined" sx={{ p: 1.4, borderRadius: 3 }}>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {driver.vehicleNumber || driver.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Vehicle number
                    </Typography>
                  </Box>
                  <Chip
                    label={`Capacity ${capacityLabel(driver.vehicleCapacityKg)}`}
                    size="small"
                    sx={{ bgcolor: 'rgba(29,78,216,0.10)', color: '#1d4ed8', fontWeight: 700 }}
                  />
                </Stack>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Working window: {buildWindowLabel(driver.shiftStartTime, driver.shiftEndTime)}
                </Typography>
              </Stack>
            </Paper>
          ))}
          {visibleDrivers.length === 0 && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                No drivers match the current filters.
              </Typography>
            </Box>
          )}
        </Stack>
      );
    }

    return (
      <Stack spacing={1.2}>
        {visibleDrivers.map((driver: DriverInfo) => (
          <Paper key={driver.driverId} variant="outlined" sx={{ p: 1.5, borderRadius: 3 }}>
            <Stack direction="row" justifyContent="space-between" spacing={1}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {driver.name}
                  </Typography>
                  {!!driver.class && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Class: {driver.class}
                    </Typography>
                  )}
                </Stack>
                <Stack spacing={0.45} sx={{ mt: 0.8 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Shift: {buildWindowLabel(driver.shiftStartTime, driver.shiftEndTime)}
                  </Typography>
                  {!!driver.maxDutyHours && (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Duty limit: {driver.maxDutyHours} h
                    </Typography>
                  )}
                  {!!driver.maxDriveHours && (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Drive limit: {driver.maxDriveHours} h
                    </Typography>
                  )}
                  {!!driver.licenceClasses?.length && (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Licences: {driver.licenceClasses.join(', ')}
                    </Typography>
                  )}
                  {!!driver.allowedVehicleTypes?.length && (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Vehicle types: {driver.allowedVehicleTypes.join(', ')}
                    </Typography>
                  )}
                </Stack>
              </Box>
              <Stack direction="row" spacing={0.6} alignItems="center">
                <Button
                  size="small"
                  variant={driverAvailability[driver.driverId] ? 'contained' : 'outlined'}
                  onClick={() =>
                    setDriverAvailability((prev) => ({
                      ...prev,
                      [driver.driverId]: !prev[driver.driverId],
                    }))
                  }
                >
                  {driverAvailability[driver.driverId] ? 'Available' : 'Unavailable'}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        ))}
      </Stack>
    );
  };

  const renderEquipmentCards = () => (
    <Stack spacing={1.2}>
      {visibleEquipment.map((equipment: any) => (
        <Paper key={equipment.equipmentId} variant="outlined" sx={{ p: 1.5, borderRadius: 3 }}>
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {equipment.type}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  ID: {equipment.equipmentId}
                </Typography>
              </Stack>
              <Stack spacing={0.45} sx={{ mt: 0.8 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Capacity: {equipment.capacity_weight_kg} kg | {equipment.capacity_volume_m3} m³
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Fuel: {equipment.fuel_type} | Range {equipment.fuel_range_km} km
                </Typography>
                {!!equipment.fleetNumber && (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Fleet: {equipment.fleetNumber}
                  </Typography>
                )}
              </Stack>
            </Box>
            <Button
              size="small"
              variant={equipmentAvailability[equipment.equipmentId] ? 'contained' : 'outlined'}
              onClick={() =>
                setEquipmentAvailability((prev) => ({
                  ...prev,
                  [equipment.equipmentId]: !prev[equipment.equipmentId],
                }))
              }
            >
              {equipmentAvailability[equipment.equipmentId] ? 'Available' : 'Unavailable'}
            </Button>
          </Stack>
        </Paper>
      ))}
    </Stack>
  );

  const renderPanelControls = () => {
    if (!panelStep) return null;
    if (panelStep === 'orders') {
      return (
        <CompactSearchSortControl
          query={searchTerm}
          onQueryChange={setSearchTerm}
          placeholder={
            isGemba
              ? 'Search by order id or address'
              : isJumboBuy
                ? 'Search by pincode, buyer, SO ID or address'
                : 'Search by SO ID, waste, asset or UoM'
          }
          sortKey={orderSortKey}
          sortDirection={orderSortDir}
          sortOptions={orderSortOptions}
          onSortKeyChange={setOrderSortKey}
          onSortDirectionChange={setOrderSortDir}
          sx={{ mt: 1 }}
        />
      );
    }

    if (panelStep === 'drivers') {
      return (
        <CompactSearchSortControl
          query={driverSearch}
          onQueryChange={setDriverSearch}
          placeholder={isGemba ? 'Search by vehicle number' : 'Search by name, class, licence or vehicle type'}
          sortKey={driverSortKey}
          sortDirection={driverSortDir}
          sortOptions={driverSortOptions}
          onSortKeyChange={setDriverSortKey}
          onSortDirectionChange={setDriverSortDir}
          sx={{ mt: 1 }}
        />
      );
    }

    return (
      <CompactSearchSortControl
        query={equipmentSearch}
        onQueryChange={setEquipmentSearch}
        placeholder="Search by type, ID, fleet or waste type"
        sortKey={equipmentSortKey}
        sortDirection={equipmentSortDir}
        sortOptions={equipmentSortOptions}
        onSortKeyChange={setEquipmentSortKey}
        onSortDirectionChange={setEquipmentSortDir}
        sx={{ mt: 1 }}
      />
    );
  };

  const renderPanelContent = () => {
    if (panelStep === 'orders') return renderOrderCards();
    if (panelStep === 'drivers') return renderDriverCards();
    if (panelStep === 'equipment') return renderEquipmentCards();
    return null;
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', width: '100vw', overflow: 'hidden' }}>
      <Sidebar {...DEFAULT_SIDEBAR_PROPS} />
      <Box sx={{ flex: 1, position: 'relative', height: '100vh', overflow: 'hidden' }}>
        <Header
          taskInfo={minimalTaskInfo}
          loading={false}
          showMetrics={verifyMetricStripItems.length > 0}
          isTaskSummary={false}
          isUserInfo={false}
          onBackToSummary={() => navigate(`/task-summary/hub/${hubId}`)}
          titleOverride="Verify Task"
          metricStripItems={verifyMetricStripItems}
        />

        <Box sx={{ position: 'absolute', top: 64, left: 0, right: 0, bottom: 0, zIndex: 1 }}>
          {hasWorkspaceData ? (
            <MapView
              taskInfo={minimalTaskInfo}
              visibleTrips={visibleTrips}
              showPolygons={isJumboBuy}
              showSequence={false}
              allowRouteLines={false}
              allowSequenceLines={false}
              showMapLegend={!isGemba && !isJumboBuy}
              showMapOptions={!isGemba && !isJumboBuy}
              showTripVehicleMarkers={false}
              editMode={false}
              selectedTouchPoints={[]}
              onMapOptionsChange={() => {}}
              onTouchPointSelect={() => {}}
              onCancelEdit={() => {}}
              onSubmitEdit={() => {}}
              focusLngLat={focusLngLat}
              maxBounds={maxBounds}
              equipmentMarkers={equipmentMarkers}
              lookups={data?.lookups}
              externalHover={null}
              externalTouchPoint={externalTouchPoint}
              activeTouchPointKey={activeTouchPointKey}
              activeTripName={isJumboBuy ? activeGroupId : null}
              onClearExternalTouchPoint={() => {
                setExternalTouchPoint(null);
                setActiveTouchPointKey(null);
              }}
              geoCorrectionTouchPoint={geoCorrectionTouchPoint}
              geoCorrectionDraftLngLat={geoCorrectionDraftLngLat}
              geoCorrectionSubmitting={geoCorrectionSubmitting}
              onStartGeoCorrection={startGeoCorrection}
              onGeoCorrectionDraft={(lngLat) => setGeoCorrectionDraftLngLat(lngLat)}
              onCancelGeoCorrection={cancelGeoCorrection}
              onConfirmGeoCorrection={confirmGeoCorrection}
            />
          ) : (
            <Box
              sx={(theme) => ({
                height: '100%',
                background:
                  theme.palette.mode === 'dark'
                    ? 'linear-gradient(180deg, rgba(22, 26, 31, 0.96) 0%, rgba(12, 15, 19, 0.98) 100%)'
                    : 'linear-gradient(180deg, rgba(241, 244, 247, 0.96) 0%, rgba(225, 231, 237, 0.98) 100%)',
              })}
            />
          )}
          {hasWorkspaceData && isJumboBuy && (
            <Paper
              elevation={3}
              sx={{
                position: 'absolute',
                top: 18,
                right: 18,
                zIndex: 8,
                px: 1.1,
                py: 0.9,
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                minWidth: 184,
              }}
            >
                <Stack spacing={0.65}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 0.2 }}>
                    Geo Loc Quality
                  </Typography>
                  <Stack direction="row" spacing={0.8} alignItems="center">
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#16a34a', border: '1px solid rgba(15,23,42,0.12)' }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Accurate
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.8} alignItems="center">
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#DE3163', border: '1px solid rgba(15,23,42,0.12)' }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Low confidence
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.8} alignItems="center">
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#e5ac62', border: '1px solid rgba(15,23,42,0.12)' }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Derived
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.8} alignItems="center">
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#7c3aed', border: '1px solid rgba(15,23,42,0.12)' }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Corrected
                  </Typography>
                </Stack>
              </Stack>
            </Paper>
          )}
        </Box>

        {hasWorkspaceData && (
          <Paper
            elevation={6}
            sx={{
              position: 'absolute',
              top: 86,
              left: 24,
              width: panelWidth,
              maxHeight: 'calc(100vh - 112px)',
              zIndex: 22,
              overflow: 'hidden',
              p: 1.1,
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'divider',
              background: 'rgba(255,255,255,0.90)',
              backdropFilter: 'blur(14px)',
              boxShadow: '0 24px 60px rgba(15,23,42,0.18)',
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6, px: 0.4 }}>
              Verification flow
            </Typography>
            <Stack spacing={0.7} sx={{ mt: 0.8 }}>
              {verifySteps.map((step, index) => {
                const active = panelStep === step.id;
                const completed = isStepCompleted(step.id);
                const unlocked = isStepUnlocked(step.id);
                return (
                  <Box
                    key={step.id}
                    component="button"
                    onClick={() => handleOpenStep(step.id)}
                    disabled={!unlocked || completed}
                    sx={{
                      width: '100%',
                      textAlign: 'left',
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: completed ? 'rgba(34,197,94,0.36)' : active ? step.accent : 'divider',
                      background: completed
                        ? 'linear-gradient(135deg, rgba(34,197,94,0.14), rgba(255,255,255,0.96))'
                        : active
                          ? `linear-gradient(135deg, ${step.accent}18, rgba(255,255,255,0.98))`
                          : unlocked
                            ? 'rgba(255,255,255,0.88)'
                            : 'rgba(241,245,249,0.88)',
                      px: 1.1,
                      py: 0.95,
                      cursor: !unlocked || completed ? 'default' : 'pointer',
                      opacity: unlocked ? 1 : 0.55,
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 30,
                          height: 30,
                          borderRadius: 2,
                          display: 'grid',
                          placeItems: 'center',
                          color: completed ? '#15803d' : step.accent,
                          bgcolor: completed ? 'rgba(34,197,94,0.14)' : `${step.accent}14`,
                          flexShrink: 0,
                        }}
                      >
                        {completed ? <CheckCircleRoundedIcon fontSize="small" /> : step.icon}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                          {index + 1}. {step.label}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
            {panelStep && activeStepDefinition ? (
              <>
                <Divider sx={{ my: 1.1 }} />
                <Box
                  sx={{
                    mx: -1.1,
                    mt: -0.1,
                    px: 1.5,
                    py: 1.2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    background: `linear-gradient(135deg, ${activeStepDefinition.accent}16, rgba(255,255,255,0.98))`,
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      {activeStepDefinition.label}
                    </Typography>
                    <Stack direction="row" spacing={0.8} alignItems="center">
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleConfirmStep(panelStep)}
                        disabled={runAlgoLoading}
                        sx={{
                          bgcolor: activeStepDefinition.accent,
                          '&:hover': {
                            bgcolor: activeStepDefinition.accent,
                            filter: 'brightness(0.95)',
                          },
                          px: 1.5,
                          borderRadius: 2.2,
                          boxShadow: 'none',
                        }}
                      >
                        {activeStepDefinition.confirmLabel}
                      </Button>
                      <IconButton size="small" onClick={() => setPanelStep(null)}>
                        <CloseRoundedIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                  {renderPanelControls()}
                </Box>

                <Box sx={{ mt: 1.1, maxHeight: 'calc(100vh - 298px)', overflowY: 'auto', pr: 0.2 }}>
                  {renderPanelContent()}
                </Box>
              </>
            ) : null}
          </Paper>
        )}

        {workspaceError ? (
          <Box sx={{ position: 'absolute', top: 96, left: 96, zIndex: 20, maxWidth: 440 }}>
            <Alert severity="error">{workspaceError}</Alert>
          </Box>
        ) : null}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={2200}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        <BlockingLoadingOverlay
          open={loading || runAlgoLoading || geoCorrectionSubmitting}
          label={runAlgoLoading ? 'Verifying task' : geoCorrectionSubmitting ? 'Updating geo location' : 'Loading verification'}
          detail={
            runAlgoLoading
              ? 'Saving the final verification state and preparing the summary view.'
              : geoCorrectionSubmitting
                ? 'Saving the corrected location and refreshing the verification workspace.'
                : 'Preparing the verification workspace.'
          }
        />
      </Box>
    </Box>
  );
};

export default VerifyTaskView;
